import { Injectable } from '@angular/core';
import type { CardDescriptorInterface } from '@zaoblako/shared/common/dtos/card/card-descriptor.dto';
import type { EntityCardHistoryQueryInterface } from '@zaoblako/shared/common/dtos/history/entity-card-history-query.dto';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { ContractHistoryPageInterface } from '@zaoblako/shared/modules/document/dtos/contract-history/contract-history-page.dto';
import type { ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import type { ContractPageInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-page.dto';
import type { ContractItemsQueryInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-items-query.dto';
import type { ContractQueryInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-query.dto';
import type { ContractSwitchStatusInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-switch-status.dto';
import type { ContractSettingsFindInterface } from '@zaoblako/shared/modules/client/dtos/contract-settings/contract-settings-find.dto';
import type { ContractSettingsFormInterface } from '@zaoblako/shared/modules/client/dtos/contract-settings/contract-settings-form.dto';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import type { ContractAnalyticalFeaturesQueryInterface } from '@zaoblako/shared/modules/document/dtos/contract-analytical-features/contract-analytical-features-query.dto';
import type { ContractAnalyticalFeaturesPageInterface } from '@zaoblako/shared/modules/document/dtos/contract-analytical-features/contract-analytical-features-page.dto';

import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ContractService extends BaseService {
  protected baseUrl = '/finance/document/contract';

  public get dropdownDescriptorApi(): string {
    return `${this.baseUrl}/dropdown-descriptor`;
  }

  public getCardDescriptor(): Observable<CardDescriptorInterface> {
    return this.http.get<CardDescriptorInterface>(`${this.baseUrl}/card-descriptor`);
  }

  public getReportDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(this.baseUrl);
  }

  public findWithPagination(queryParams?: ContractQueryInterface): Observable<ContractPageInterface> {
    return this.findRegistryPage(queryParams);
  }

  public findRegistryPage(queryParams?: ContractItemsQueryInterface): Observable<ContractPageInterface> {
    return this.http.get<ContractPageInterface>(`${this.baseUrl}/items`, { params: this.objectToHttpParams(queryParams) });
  }

  public switchStatus(id: number, body: ContractSwitchStatusInterface): Observable<ContractFindInterface> {
    return this.http.post<ContractFindInterface>(`${this.baseUrl}/${id}/switch-status`, body);
  }

  public getHistoryById(
    id: number,
    queryParams: EntityCardHistoryQueryInterface | PaginationQueryInterface
  ): Observable<ContractHistoryPageInterface> {
    return this.http.get<ContractHistoryPageInterface>(`${this.baseUrl}/${id}/history`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public restore(id: number): Observable<ContractFindInterface> {
    return this.http.post<ContractFindInterface>(`${this.baseUrl}/${id}/restore`, {});
  }

  public upload1c(queryParams?: ContractQueryInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/upload-1c`, {}, { params: this.objectToHttpParams(queryParams) });
  }

  public download1c(queryParams?: ContractQueryInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/download-1c`, {}, { params: this.objectToHttpParams(queryParams) });
  }

  public getSettings(): Observable<ContractSettingsFindInterface> {
    return this.http.get<ContractSettingsFindInterface>(`${this.baseUrl}/settings`);
  }

  public setSettings(body: ContractSettingsFormInterface): Observable<ContractSettingsFindInterface> {
    return this.http.put<ContractSettingsFindInterface>(`${this.baseUrl}/settings`, body);
  }

  public toFillAnalytics(
    queryParams: ContractAnalyticalFeaturesQueryInterface
  ): Observable<ContractAnalyticalFeaturesPageInterface> {
    return this.http.get<ContractAnalyticalFeaturesPageInterface>(`${this.baseUrl}/to-fill-analytics`, {
      params: this.objectToHttpParams(queryParams)
    });
  }
}
